import { useState } from 'react';
import { logError } from 'utils/logger';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { isNullOrUndefined } from 'utils/typeGuargs';
import { ProposalMenu } from '@trnsact/trnsact-shared-types';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';
import {
  CREATE_PROPOSAL_MENU,
  UPDATE_PROPOSAL_MENU,
  CREATE_PROPOSAL_MENU_OPTION,
  UPDATE_PROPOSAL_MENU_OPTION,
} from '../api';
import { MenuOptionToAddEdit } from '../types';
import { menuConstructorSelectors } from '../model';

export function useSavingMenu() {
  const proposalProductConfigurationsInMenu = useSelector(menuConstructorSelectors.proposalProductConfigurationsInMenu);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [createProposalMenu] = useMutation(CREATE_PROPOSAL_MENU);
  const [updateProposalMenu] = useMutation(UPDATE_PROPOSAL_MENU);
  const [createProposalMenuOption] = useMutation(CREATE_PROPOSAL_MENU_OPTION);
  const [updateProposalMenuOption] = useMutation(UPDATE_PROPOSAL_MENU_OPTION);

  const getProductMenuOptionLinkingCriteria = (product: ProposalProduct, menuName: string) => {
    const { cost, retailCost, markup, ...otherConfigValues } =
      proposalProductConfigurationsInMenu?.[menuName]?.[product.proposalProductId];

    return {
      productId: product.proposalProductId,
      cost: Number(cost) ?? product.cost,
      retailCost: Number(retailCost) ?? product.retailCost,
      markup: {
        type: markup?.type ?? product.markup.type,
        markup: Number(markup?.markup) ?? product.markup.markup,
      },
      config: {
        criteriaValues: otherConfigValues ?? [],
      },
    };
  };

  const updateMenu = async (proposalMenu: ProposalMenu): Promise<{ success: boolean }> => {
    const getBaseMenuOptionToCreateOrUpdate = (menuOption: MenuOptionToAddEdit) => ({
      name: menuOption.name,
      type: menuOption.type,
      titleColor: menuOption.titleColor,
      description: menuOption.description,
      titleBgColor: menuOption.titleBgColor,
      proposalProductMenuOptionLinkingCriteria: (menuOption.products as ProposalProduct[])?.map(product =>
        getProductMenuOptionLinkingCriteria(product, menuOption.name!)
      ),
    });

    const createMenuOption = async (menuOption: MenuOptionToAddEdit) => {
      try {
        const result = await createProposalMenuOption({
          variables: {
            input: getBaseMenuOptionToCreateOrUpdate(menuOption),
          },
        });

        return result?.data?.createProposalMenuOption?.proposalMenuOptionId as string | null;
      } catch (error) {
        console.error(error);
        return null;
      }
    };

    const updateMenuOption = async (menuOption: MenuOptionToAddEdit) => {
      try {
        const result = await updateProposalMenuOption({
          variables: {
            input: {
              proposalMenuOptionId: menuOption.proposalMenuOptionId,
              ...getBaseMenuOptionToCreateOrUpdate(menuOption),
            },
          },
        });

        return result?.data?.updateProposalMenuOption?.proposalMenuOptionId as string | undefined;
      } catch (error) {
        console.error(error);
        return null;
      }
    };

    try {
      setIsLoading(true);

      const menuOptionIds = await Promise.all(
        (proposalMenu.menuOptions ?? []).map((menuOption: any) =>
          menuOption.proposalMenuOptionId ? updateMenuOption(menuOption) : createMenuOption(menuOption)
        )
      );

      if (menuOptionIds.some(isNullOrUndefined)) throw new Error('Error creating or updating menu option');

      const linkedToCriteria = proposalMenu.linkedToCriterias?.map(({ partnerLinkId, isReadOnly }) => ({
        isReadOnly,
        partnerLinkId,
      }));

      const menuPayload = {
        name: proposalMenu.name,
        menuOptionIdsToLink: menuOptionIds,
        linkedToCriterias: linkedToCriteria,
        description: proposalMenu.description,
        proposalMenuId: proposalMenu.proposalMenuId,
      };

      await updateProposalMenu({ variables: { input: menuPayload } });

      return { success: true };
    } catch (error) {
      logError(error);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const createMenu = async (proposalMenu: ProposalMenu): Promise<{ success: boolean }> => {
    setIsLoading(true);

    const formatMenuOptionForPayload = (menuOption: any) => {
      return {
        name: menuOption.name,
        description: menuOption.description,
        titleColor: menuOption.titleColor,
        titleBgColor: menuOption.titleBgColor,
        type: menuOption.type,
        proposalProductMenuOptionLinkingCriteria: menuOption.products?.map((product: any) =>
          getProductMenuOptionLinkingCriteria(product, menuOption.name!)
        ),
      };
    };

    try {
      const menuPayload = {
        name: proposalMenu.name,
        description: proposalMenu.description,
        newMenuOptions: proposalMenu.menuOptions?.map(formatMenuOptionForPayload),
        linkedToCriterias: proposalMenu.linkedToCriterias?.map(linkedToCriteria => ({
          partnerLinkId: linkedToCriteria.partnerLinkId,
          isReadOnly: linkedToCriteria.isReadOnly,
        })),
      };

      await createProposalMenu({
        variables: {
          input: menuPayload,
        },
      });

      return { success: true };
    } catch (error) {
      console.error(error);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateMenu,
    createMenu,
    isLoading,
  };
}
